/**
 * `enroll:<person>/<course>`, one learner's marks and attendance in one course
 * as a visit (P10, CASE-027). A course row of the academic record visits it,
 * never the course: the trail is People, the person, then the course, and
 * Return steps back one level.
 *
 * Its one Fact reads the course's roll, the read A4's roster holds, and draws
 * A4's learner marks in place (C28 `p-learner`). It writes nothing: marks are
 * entered in the course. A failed read keeps its box and draws no grade and no
 * mark, never an empty one (R1, R3). A course outside this person's record, or
 * a viewer outside the registrar shell, is refused and named nothing (R1, P4).
 */
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router";

import { LearnerMarks, type RosterLearner, type RosterSession } from "@/components/attendance/roster/LearnerMarks";
import type { Box, FactBox, Read } from "@/components/ui/box.schema";
import { ApiError } from "@/lib/api/client";
import { attendanceApi, type CourseAttendance } from "@/lib/api/attendance";
import { academicRecordQuery, type AcademicRecordCourse } from "@/lib/api/students";
import type { VisitLevel } from "@/lib/visit/address";
import { shellOf, type VisitResolution } from "@/lib/visit/registry";

import { personDisplayName } from "../person-record";
import { imam, queryRead } from "./commit";
import { usePersonDetail } from "./person-visit";
import { courseState } from "./student-boxes";
import { studentCopy as copy, studentText } from "./student-copy";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface Enrollment {
  course: AcademicRecordCourse;
  learner: RosterLearner;
  held: RosterSession[];
}

/** The roll's read, narrowed to this learner; a learner the roster no longer holds draws no marks (R1). */
function enrollmentRead(read: Read<CourseAttendance>, course: AcademicRecordCourse, studentId: string): Read<Enrollment> {
  const narrow = (value: CourseAttendance) => {
    const learner = value.roster.find((each) => each.student_id === studentId);
    return learner ? { course, learner, held: value.sessions.filter((session) => session.status === "held") } : null;
  };
  if (read.status === "ready" || read.status === "stale") {
    const value = narrow(read.status === "ready" ? read.value : read.last);
    if (!value) return { status: "empty", sentence: copy.notOnRoster };
    return read.status === "ready" ? { status: "ready", value, readAt: read.readAt } : { ...read, last: value };
  }
  if (read.status === "failed") return { ...read, sentence: copy.marksUnavailable, detail: copy.marksUnavailableDetail };
  return read;
}

function resultWords(course: AcademicRecordCourse): string {
  const word = courseState(course).word;
  return course.result.state === "certified" && course.result.letter ? studentText.result(word) : word;
}

export function useEnrollVisit(level: VisitLevel | null): VisitResolution | null {
  const location = useLocation();
  const shell = shellOf(location.pathname);
  const registrar = shell === "admin" || shell === "adminTeaching";
  const named = level && registrar && UUID.test(level.id) && level.job && UUID.test(level.job);
  const personId = named ? level.id : null;
  const courseId = named ? (level.job as string) : null;

  const detail = usePersonDetail(personId);
  const studentId = detail.data?.student_record?.id ?? null;
  const academic = useQuery({ ...academicRecordQuery(studentId ?? "none"), enabled: Boolean(studentId), retry: false });
  const course = academic.data?.years
    .flatMap((year) => year.terms.flatMap((term) => term.courses))
    .find((each) => each.course_id === courseId) ?? null;
  const roll = useQuery({
    queryKey: ["attendance", "course", courseId],
    queryFn: () => attendanceApi.getCourseAttendance(courseId as string),
    enabled: Boolean(course),
    retry: false,
  });

  if (!level) return null;
  if (!personId || !courseId) return { status: "refused" };
  if (detail.error instanceof ApiError && (detail.error.status === 404 || detail.error.status === 403)) return { status: "refused" };
  if (detail.data && detail.data.person.id !== personId) return { status: "refused" };
  if ((!detail.data && !detail.isError) || (studentId && academic.isPending)) return { status: "loading" };
  // Only a course on this person's own record is theirs to read here.
  if (detail.data && (!studentId || (academic.data && !course))) return { status: "refused" };

  // A failed read of the person or their record keeps the box, with no mark drawn (R3).
  const unread = (retry: () => unknown): Read<Enrollment> =>
    ({ status: "failed", sentence: copy.marksUnavailable, detail: copy.marksUnavailableDetail, retry: () => void retry() });
  const name = detail.data ? (detail.data.person.preferred_name ?? personDisplayName(detail.data)) : null;
  const read: Read<Enrollment> = !detail.data
    ? unread(detail.refetch)
    : course && studentId
      ? enrollmentRead(queryRead(roll, copy.marksUnavailable, copy.marksUnavailableDetail), course, studentId)
      : unread(academic.refetch);
  const marks: FactBox<Enrollment> = {
    id: `enroll-${personId}-${courseId}`,
    kind: "fact",
    job: copy.enrollJob,
    label: copy.marksAndAttendance,
    owner: imam,
    fold: "never",
    read,
    detail: (value) => resultWords(value.course),
    openDetail: (value) => studentText.marksReadOnly(resultWords(value.course)),
    figures: (value) => [{ value: value.learner.stats.present + value.learner.stats.late, unit: studentText.attended(value.held.length) }],
    body: (value) => <LearnerMarks learner={value.learner} held={value.held} timezone={value.course.schedule.timezone ?? "UTC"} />,
  };
  return {
    status: "ready",
    subject: name && course ? studentText.enrollSubject(name, course.title) : (name ?? copy.enrollment),
    boxes: [marks as unknown as Box],
    opens: [marks.id],
  };
}
